/**
 * The shell's bundled type, handed to a page that runs in a sandboxed frame.
 *
 * `src/fonts.ts` puts Inter and Noto Sans SC into the shell's own document. A surface runs in `srcdoc` with an
 * opaque origin and shares nothing with it, so without this its text falls back to whatever the system has and
 * no longer matches the shell around it.
 *
 * `src/surface-fonts.css` imports the same two families and nothing else, and the bundler emits it as one style
 * sheet beside the woff2 files it names (`?url`). The frame links that sheet, so it is fetched once and kept
 * for every page; the browser then loads only the unicode-range slices a page's own glyphs need. Its policy
 * lets `style-src` reach the sheet and `font-src` reach the font directories, and the font files answer with
 * `Access-Control-Allow-Origin` (`workbench/server/app.py` `ShellAssets`), which a font fetch from an opaque
 * origin needs.
 */

import fontsHref from "../surface-fonts.css?url";
import interHref from "@fontsource-variable/inter/files/inter-latin-wght-normal.woff2?url";
import notoHref from "@fontsource-variable/noto-sans-sc/files/noto-sans-sc-latin-wght-normal.woff2?url";

export interface BundledType {
  /** The style sheet of `@font-face` rules, as an absolute address; empty where there is none. */
  href: string;
  /** The style sheet's directory, as `style-src` lists it. */
  sources: string[];
  /** Font directories can differ from the sheet's in Vite development. */
  fontSources?: string[];
}

/** The shell's own type, addressed from `origin`. */
export function bundledType(origin: string = window.location.origin): BundledType {
  try {
    const url = new URL(fontsHref, `${origin}/`);
    if (url.protocol !== "http:" && url.protocol !== "https:") return { href: "", sources: [] };
    const fontSources = [...new Set([interHref, notoHref].map(href => new URL(".", new URL(href, url)).href))];
    return { href: url.href, sources: [new URL(".", url).href], fontSources };
  } catch {
    return { href: "", sources: [] };
  }
}
